import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import apiService from '../services/api'
import { mergeTerminology, resolveTerminology } from '../config/workspaceTerminology'
import { useAuth } from './AuthContext'
import { useDirection } from './DirectionContext'

const WorkspaceContext = createContext()

export function WorkspaceProvider({ children }) {
  // Get workspace switching state from auth context
  // WorkspaceProvider is only used inside ProtectedRoute which has AuthProvider
  const auth = useAuth()
  const isWorkspaceSwitched = auth?.isWorkspaceSwitched || false
  const ownerAccountId = auth?.ownerAccountId || null

  // Active dashboard language ('en' | 'ar') drives which terminology set we
  // show.  DirectionProvider sits above WorkspaceProvider in the tree, so
  // this is always available.  When the user toggles language, `lang`
  // changes and the resolved terminology recomputes — re-labeling the whole
  // app live.
  const { lang } = useDirection()

  const [workspaceType, setWorkspaceType] = useState('legal')
  const [customTerminology, setCustomTerminology] = useState({})
  // `bilingualTerminology` holds BOTH languages (template merged with custom
  // overrides).  `terminology` (below) is the flattened, active-language view
  // that every consumer reads — so getEntity()/t()/getAction() keep their
  // existing single-value API while becoming language-aware for free.
  const [bilingualTerminology, setBilingualTerminology] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Merge template with custom terminology whenever they change → bilingual.
  useEffect(() => {
    if (workspaceType) {
      setBilingualTerminology(mergeTerminology(workspaceType, customTerminology))
    }
  }, [workspaceType, customTerminology])

  // Flatten the bilingual structure down to the active language.  Recomputes
  // when the language toggles, so the entire dashboard re-labels instantly.
  const terminology = useMemo(
    () => resolveTerminology(bilingualTerminology, lang),
    [bilingualTerminology, lang]
  )

  // Load terminology function - uses latest workspace switching state
  const loadTerminology = useCallback(async () => {
    try {
      setLoading(true)
      
      // If workspace is switched, pass owner_account_id to get owner's terminology
      const params = {}
      if (isWorkspaceSwitched && ownerAccountId) {
        params.owner_account_id = ownerAccountId
      }
      
      const response = await apiService.get('/workspace/terminology/', { params })

      if (response.success) {
        const data = response.data.data || response.data
        setWorkspaceType(data.workspace_type || 'legal')
        setCustomTerminology(data.custom_terminology || {})
      } else {
        // If fails, use default
        setWorkspaceType('legal')
        setCustomTerminology({})
      }
    } catch (error) {
      console.error('Error loading terminology:', error)
      // Use defaults on error
      setWorkspaceType('legal')
      setCustomTerminology({})
      setError(error)
    } finally {
      setLoading(false)
    }
  }, [isWorkspaceSwitched, ownerAccountId])

  // Load terminology from API on mount and when workspace switching changes
  useEffect(() => {
    loadTerminology()
  }, [loadTerminology])

  const updateTerminology = async (newType, newCustomTerminology = {}) => {
    try {
      const payload = {
        workspace_type: newType,
        custom_terminology: newCustomTerminology
      }

      const response = await apiService.put('/workspace/terminology/', payload)

      if (response.success) {
        const data = response.data.data || response.data
        setWorkspaceType(data.workspace_type)
        setCustomTerminology(data.custom_terminology || {})
        return { success: true }
      } else {
        return { success: false, error: response.error?.message || 'Failed to update' }
      }
    } catch (error) {
      console.error('Error updating terminology:', error)
      return { success: false, error: error.message }
    }
  }

  const resetTerminology = async () => {
    try {
      const response = await apiService.post('/workspace/terminology/reset/')

      if (response.success) {
        setCustomTerminology({})
        return { success: true }
      } else {
        return { success: false, error: response.error?.message || 'Failed to reset' }
      }
    } catch (error) {
      console.error('Error resetting terminology:', error)
      return { success: false, error: error.message }
    }
  }

  // Translate function - gets a specific term
  const t = (path, defaultValue = null) => {
    if (!terminology) return defaultValue || path

    // Handle different path formats
    // "case" -> "cases.plural"
    // "case.singular" -> direct access
    // "case.plural" -> direct access
    
    const parts = path.split('.')
    
    // If just entity name (e.g., "case"), default to plural
    if (parts.length === 1) {
      return terminology.entities?.[parts[0]]?.plural || defaultValue || path
    }
    
    // Navigate the path
    let result = terminology
    for (const part of parts) {
      if (result && typeof result === 'object') {
        result = result[part]
      } else {
        return defaultValue || path
      }
    }
    
    return result || defaultValue || path
  }

  // Get entity (returns both singular and plural) for the ACTIVE language.
  // Pass an explicit `forLang` ('en' | 'ar') to force a language regardless
  // of the current UI language (rarely needed — e.g. building an email in a
  // fixed language).
  const getEntity = (entityName, forLang = null) => {
    if (forLang && bilingualTerminology?.entities?.[entityName]) {
      const terms = bilingualTerminology.entities[entityName]
      const picked = terms[forLang] || terms.en
      if (picked) return picked
    }
    return terminology?.entities?.[entityName] || {
      singular: entityName,
      plural: entityName + 's',
    }
  }

  // Get action label for the ACTIVE language (or an explicit one).
  const getAction = (actionName, forLang = null) => {
    if (forLang && bilingualTerminology?.actions?.[actionName]) {
      const a = bilingualTerminology.actions[actionName]
      return a[forLang] || a.en || actionName
    }
    return terminology?.actions?.[actionName] || actionName
  }

  const value = {
    workspaceType,
    customTerminology,
    terminology,            // flat, active-language (what consumers read)
    bilingualTerminology,   // both languages (for editors / explicit lookups)
    lang,                   // active language, surfaced for convenience
    loading,
    error,
    updateTerminology,
    resetTerminology,
    loadTerminology,
    t,
    getEntity,
    getAction
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading workspace...</span>
        </div>
      </div>
    )
  }

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  )
}

// Custom hook to use workspace context
export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider')
  }
  
  return context
}

export default WorkspaceContext

